import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Alert,
  Chip,
  IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import CreateOrganizationDialog from '../../components/organization/dialogs/CreateOrganizationDialog';
import RenameOrganizationDialog from '../../components/organization/dialogs/RenameOrganizationDialog';
import DeleteOrganizationDialog from '../../components/organization/dialogs/DeleteOrganizationDialog';

const OrganizationsSettings = () => {
  const { organizations, selectedOrganization, isOwner, loading, error } = useOrganization();
  const { isDemoMode } = useAuth();
  const { can } = usePermissions();
  // Free is one workspace; the server decides (per owner, Free workspaces only) and this only
  // saves somebody filling in a name to be refused
  const { plan, limits, showPlanLimit } = useEntitlements();
  const workspaceLocked = limits?.workspaces != null;

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);

  const handleRenameClick = (org) => {
    setSelectedOrg(org);
    setRenameDialogOpen(true);
  };

  const handleDeleteClick = (org) => {
    setSelectedOrg(org);
    setDeleteDialogOpen(true);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Your Organizations
        </Typography>
        {can.modifyOrg && (
          <Button
            variant="contained"
            startIcon={workspaceLocked ? <LockIcon /> : <AddIcon />}
            onClick={() => (workspaceLocked
              ? showPlanLimit({ code: 'PLAN_LIMIT', limit: 'workspaces', plan })
              : setCreateDialogOpen(true))}
          >
            Create Organization
          </Button>
        )}
      </Box>

      {isDemoMode && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Create an account to name your organisation and invite people into it.
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Organizations List */}
      {organizations.length === 0 ? (
        <Alert severity="info">
          No organizations found. Create your first organization to get started.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {organizations.map((org) => {
            const isUserOwner = isOwner(org.id);
            const isSelected = selectedOrganization?.id === org.id;

            return (
              <Grid item xs={12} sm={6} md={4} key={org.id}>
                <Card
                  sx={{
                    height: '100%',
                    border: isSelected ? 2 : 0,
                    borderColor: 'primary.main'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" component="div">
                        {org.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {isSelected && (
                          <Chip label="Selected" color="primary" size="small" />
                        )}
                        {isUserOwner && (
                          <Chip label="Owner" color="success" size="small" />
                        )}
                        {!isUserOwner && (
                          <Chip label="Member" size="small" />
                        )}
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Created: {new Date(org.createdAt).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                  {(can.modifyOrg || can.deleteOrg) && (
                    <CardActions>
                      {can.modifyOrg && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleRenameClick(org)}
                          title="Rename"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {can.deleteOrg && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(org)}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </CardActions>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Dialogs */}
      <CreateOrganizationDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />

      {selectedOrg && (
        <>
          <RenameOrganizationDialog
            open={renameDialogOpen}
            onClose={() => {
              setRenameDialogOpen(false);
              setSelectedOrg(null);
            }}
            organization={selectedOrg}
          />

          <DeleteOrganizationDialog
            open={deleteDialogOpen}
            onClose={() => {
              setDeleteDialogOpen(false);
              setSelectedOrg(null);
            }}
            organization={selectedOrg}
          />
        </>
      )}
    </Box>
  );
};

export default OrganizationsSettings;
